import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Building, Users, Activity, TrendingUp, Server, Shield, Zap, Loader2, AlertCircle } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: number | string;
  description?: string;
  icon: React.ElementType;
  trend?: string;
}

function MetricCard({ title, value, description, icon: Icon, trend }: MetricCardProps) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {trend && <p className="text-xs text-muted-foreground mt-1">{trend}</p>}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="text-center py-12">
      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
      <p className="text-muted-foreground">Loading platform metrics...</p>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="text-center py-12">
      <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-3" />
      <p className="text-destructive font-medium mb-2">Unable to load platform metrics</p>
      <p className="text-sm text-muted-foreground mb-4">Please check your connection and try again</p>
      <Button onClick={onRetry} variant="outline" size="sm">Retry</Button>
    </div>
  );
}

export function PlatformMetrics() {
  const [includeAdmins, setIncludeAdmins] = useState(false);
  const [activeView, setActiveView] = useState<'customer' | 'system'>('customer');
  
  // Restore toggle from sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem('adminMetrics_includeAdmins');
    if (saved !== null) setIncludeAdmins(saved === 'true');
  }, []);
  
  // Date calculations
  const now = new Date();
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  const lastMonthISO = lastMonth.toISOString();

  // CUSTOMER METRICS QUERY (with conditional admin filter)
  const { 
    data: customerMetrics, 
    isLoading: customerLoading, 
    isError: customerError,
    refetch: refetchCustomer
  } = useQuery({
    queryKey: ['platform-metrics', 'customer', includeAdmins],
    queryFn: async () => {
      const [
        totalOrganizationsResult,
        totalUsersResult,
        activeUsersResult,
        { count: lastMonthOrganizations }
      ] = await Promise.all([
        supabase.rpc('get_total_organization_count' as any),
        includeAdmins 
          ? supabase.rpc('get_total_user_count' as any)
          : supabase.rpc('get_total_customer_count' as any),
        includeAdmins 
          ? supabase.rpc('get_active_user_count' as any, { days_back: 30 })
          : supabase.rpc('get_active_customer_count' as any, { days_back: 30 }),
        supabase.from('teams').select('*', { count: 'exact', head: true }).lt('created_at', lastMonthISO)
      ]);

      const totalOrganizations = totalOrganizationsResult.data ?? 0;
      const totalUsers = totalUsersResult.data ?? 0;

      const orgChange = lastMonthOrganizations && lastMonthOrganizations > 0
        ? Math.round(((totalOrganizations! - lastMonthOrganizations) / lastMonthOrganizations) * 100)
        : 0;

      return {
        totalOrganizations: totalOrganizations || 0,
        totalUsers: totalUsers || 0,
        activeUsers: activeUsersResult.data ?? 0,
        organizationChange: orgChange
      };
    },
    staleTime: 120000,
    gcTime: 300000,
    retry: 2
  });

  // SYSTEM HEALTH QUERY (unfiltered)
  const { data: systemHealth, isLoading: systemLoading } = useQuery({
    queryKey: ['platform-metrics', 'system'],
    queryFn: async () => {
      const [
        totalSystemUsersResult,
        activeSystemUsersResult,
        { count: totalMemberships }
      ] = await Promise.all([
        supabase.rpc('get_total_user_count' as any),
        supabase.rpc('get_active_user_count' as any, { days_back: 30 }),
        supabase.from('team_members').select('*', { count: 'exact', head: true })
      ]);

      const totalSystemUsers = totalSystemUsersResult.data ?? 0;
      
      return {
        totalSystemUsers,
        activeSystemUsers: activeSystemUsersResult.data ?? 0,
        totalMemberships: totalMemberships || 0
      };
    },
    enabled: activeView === 'system',
    staleTime: 120000,
    gcTime: 300000,
    retry: 2
  });

  // ADMIN ACTIVITY QUERY
  const { data: adminActivity, isLoading: adminLoading } = useQuery({
    queryKey: ['platform-metrics', 'admin-activity'],
    queryFn: async () => {
      const [
        totalAdminsResult,
        activeAdminsResult,
        { count: superAdmins },
        { count: adminActions }
      ] = await Promise.all([
        supabase.rpc('get_total_admin_count' as any),
        supabase.rpc('get_active_admin_count' as any, { days_back: 30 }),
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('platform_admin_role', 'super_admin'),
        supabase.from('platform_admin_audit').select('*', { count: 'exact', head: true })
          .gte('performed_at', thirtyDaysAgo)
      ]);

      const totalAdmins = totalAdminsResult.data ?? 0;
      
      return {
        totalAdmins,
        activeAdmins: activeAdminsResult.data ?? 0,
        superAdmins: superAdmins || 0,
        adminActions: adminActions || 0
      };
    },
    enabled: activeView === 'system',
    staleTime: 120000,
    gcTime: 300000,
    retry: 2
  });

  if (customerLoading) return <LoadingState />;
  if (customerError) return <ErrorState onRetry={refetchCustomer} />;

  const handleToggleChange = (checked: boolean) => {
    setIncludeAdmins(checked);
    sessionStorage.setItem('adminMetrics_includeAdmins', String(checked));
  };

  const customerStats = [
    {
      title: 'Total Organizations',
      value: customerMetrics?.totalOrganizations || 0,
      icon: Building,
      trend: `${(customerMetrics?.organizationChange || 0) >= 0 ? '+' : ''}${customerMetrics?.organizationChange || 0}% from last month`
    },
    {
      title: 'Total Users',
      value: customerMetrics?.totalUsers || 0,
      icon: Users,
      description: includeAdmins ? 'All registered users' : 'Customer users only'
    },
    {
      title: 'Active Users (30d)',
      value: customerMetrics?.activeUsers || 0,
      icon: Activity,
      description: 'Users active in last 30 days'
    },
    {
      title: 'Active User Rate',
      value: (customerMetrics?.totalUsers ?? 0) > 0 && (customerMetrics?.activeUsers ?? 0) > 0
        ? `${Math.round(((customerMetrics?.activeUsers ?? 0) / (customerMetrics?.totalUsers ?? 0)) * 100)}%`
        : '0%',
      icon: TrendingUp,
      description: 'Engagement rate'
    }
  ];

  const systemStats = [
    {
      title: 'Total System Users',
      value: systemHealth?.totalSystemUsers || 0,
      icon: Server,
      description: 'All users including admins'
    },
    {
      title: 'Active System Users',
      value: systemHealth?.activeSystemUsers || 0,
      icon: Activity,
      description: 'Active in last 30 days'
    },
    {
      title: 'Total Memberships',
      value: systemHealth?.totalMemberships || 0,
      icon: Users,
      description: 'Team member relationships'
    },
    {
      title: 'System Load',
      value: systemHealth?.totalMemberships
        ? systemHealth.totalMemberships > 1000 ? 'High' : systemHealth.totalMemberships > 500 ? 'Normal' : 'Low'
        : 'Unknown',
      icon: Zap,
      description: 'Current capacity usage'
    }
  ];

  const adminStats = [
    {
      title: 'Platform Admins',
      value: adminActivity?.totalAdmins || 0,
      icon: Shield,
      description: 'Total admin accounts'
    },
    {
      title: 'Active Admins (30d)',
      value: adminActivity?.activeAdmins || 0,
      icon: Users,
      description: 'Admins active recently'
    },
    {
      title: 'Super Admins',
      value: adminActivity?.superAdmins || 0,
      icon: Shield,
      description: 'Full platform access'
    },
    {
      title: 'Admin Actions (30d)',
      value: adminActivity?.adminActions || 0,
      icon: Activity,
      description: 'Audit log entries'
    }
  ];

  return (
    <Tabs 
      defaultValue="customer" 
      onValueChange={(value) => setActiveView(value as 'customer' | 'system')}
      className="space-y-4"
    >
      <TabsList>
        <TabsTrigger value="customer">Customer Metrics</TabsTrigger>
        <TabsTrigger value="system">System Health</TabsTrigger>
      </TabsList>
      
      <TabsContent value="customer" className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b">
          <div>
            <h3 className="text-lg font-medium">Customer Metrics</h3>
            <p className="text-sm text-muted-foreground">Key performance indicators for customer engagement</p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={includeAdmins ? 'default' : 'secondary'} className="shrink-0">
              {includeAdmins ? 'All Users' : 'Customer Metrics'}
            </Badge>
            <div className="flex items-center gap-2">
              <label htmlFor="include-admins" className="text-sm font-medium cursor-pointer whitespace-nowrap">
                Include Platform Admins
              </label>
              <Switch
                id="include-admins"
                checked={includeAdmins}
                onCheckedChange={handleToggleChange}
                disabled={customerLoading}
                aria-label="Include platform administrators in customer metrics"
              />
            </div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {customerStats.map((stat) => <MetricCard key={stat.title} {...stat} />)}
        </div>
      </TabsContent>
      
      <TabsContent value="system" className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between pb-2 border-b">
            <div>
              <h3 className="text-lg font-medium">System Metrics</h3>
              <p className="text-sm text-muted-foreground">Platform-wide operational metrics</p>
            </div>
            <Badge variant="outline">System-Wide</Badge>
          </div>
          {systemLoading ? (
            <LoadingState />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {systemStats.map((stat) => <MetricCard key={stat.title} {...stat} />)}
            </div>
          )}
        </div>
        
        <div className="space-y-4">
          <div className="pb-2 border-b">
            <h3 className="text-lg font-medium">Admin Activity</h3>
            <p className="text-sm text-muted-foreground">Platform administrator usage and actions</p>
          </div>
          {adminLoading ? (
            <LoadingState />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {adminStats.map((stat) => <MetricCard key={stat.title} {...stat} />)}
            </div>
          )}
        </div>
      </TabsContent>
    </Tabs>
  );
}
